import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "../../components/ui/Icon.js";

interface ExchangeOption {
  id: string;
  name: string;
  initial: string;
  color: string;
  dark?: boolean;
}

const EXCHANGES: ExchangeOption[] = [
  { id: "binance", name: "Binance", initial: "B", color: "#F3BA2F" },
  { id: "bybit", name: "Bybit", initial: "BY", color: "#F7A600" },
  { id: "okx", name: "OKX", initial: "OK", color: "#0A0B0D", dark: true },
  { id: "kraken", name: "Kraken", initial: "KR", color: "#5741D9" },
  { id: "coinbase", name: "Coinbase", initial: "CB", color: "#0052FF" },
  { id: "kucoin", name: "KuCoin", initial: "KU", color: "#01BC8D" },
  { id: "gateio", name: "Gate.io", initial: "G", color: "#2354E6" },
  { id: "bitget", name: "Bitget", initial: "BG", color: "#00C2FF" },
];

const STEPS = [
  { n: 1, label: "Connect exchange" },
  { n: 2, label: "Watchlist" },
  { n: 3, label: "Risk preferences" },
  { n: 4, label: "Notifications" },
] as const;

export function OnboardingPage() {
  const navigate = useNavigate();
  const [selectedExchange, setSelectedExchange] = useState<string>("binance");

  function skipForNow(): void {
    navigate("/", { replace: true });
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100vh",
        background: "var(--bg)",
        color: "var(--text)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          height: 56,
          padding: "0 24px",
          borderBottom: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="se-logo-mark">
            <span>S</span>
          </div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>SpotEdge</div>
        </div>
        <button type="button" className="se-btn se-btn-ghost se-btn-sm" onClick={skipForNow}>
          Skip for now
        </button>
      </div>

      <div
        style={{
          flex: 1,
          overflow: "auto",
          display: "flex",
          justifyContent: "center",
          padding: "40px 32px",
        }}
      >
        <div style={{ width: "100%", maxWidth: 920 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32 }}>
            {STEPS.map((s, i) => (
              <div key={s.n} style={{ display: "flex", alignItems: "center", gap: 8, flex: i < 3 ? "1 1 auto" : "0 0 auto" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 999,
                      background: s.n === 1 ? "var(--primary)" : "var(--surface-2)",
                      border: s.n === 1 ? "1px solid var(--primary)" : "1px solid var(--border)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      fontWeight: 600,
                      color: s.n === 1 ? "white" : "var(--text-muted)",
                    }}
                  >
                    {s.n}
                  </div>
                  <span
                    style={{
                      fontSize: 12,
                      color: s.n === 1 ? "var(--text)" : "var(--text-muted)",
                      fontWeight: s.n === 1 ? 600 : 400,
                    }}
                  >
                    {s.label}
                  </span>
                </div>
                {i < 3 ? <div style={{ flex: 1, height: 1, background: "var(--border)" }} /> : null}
              </div>
            ))}
          </div>

          <h1 style={{ fontSize: 26, fontWeight: 600, margin: 0 }}>
            Connect your first exchange
          </h1>
          <p className="muted" style={{ fontSize: 13, marginTop: 6, marginBottom: 28 }}>
            Choose where you trade. We'll sync your spot portfolio and rank AI
            signals for the symbols you watch.
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 12,
              marginBottom: 32,
            }}
          >
            {EXCHANGES.map((ex) => {
              const active = selectedExchange === ex.id;
              return (
                <button
                  key={ex.id}
                  type="button"
                  onClick={() => setSelectedExchange(ex.id)}
                  className="se-card"
                  style={{
                    padding: 18,
                    textAlign: "left",
                    cursor: "pointer",
                    position: "relative",
                    border: active ? "1px solid var(--primary)" : "1px solid var(--border)",
                    background: active ? "rgba(59, 130, 246, 0.04)" : "var(--surface)",
                    transition: "border-color 0.12s",
                  }}
                >
                  {active ? (
                    <div
                      style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        width: 20,
                        height: 20,
                        borderRadius: 999,
                        background: "var(--primary)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon name="check" size={12} style={{ color: "white" }} />
                    </div>
                  ) : null}
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: ex.dark ? "#0A0B0D" : ex.color,
                      border: ex.dark ? "1px solid var(--border-soft)" : "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: 13,
                      fontWeight: 700,
                      marginBottom: 10,
                    }}
                  >
                    {ex.initial}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{ex.name}</div>
                  <div className="muted" style={{ fontSize: 10, marginTop: 2 }}>
                    Spot markets
                  </div>
                </button>
              );
            })}
          </div>

          <div className="se-card" style={{ padding: 0 }}>
            <div
              style={{
                padding: "14px 18px",
                borderBottom: "1px solid var(--border)",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  background: EXCHANGES.find((e) => e.id === selectedExchange)?.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 700,
                  color: "white",
                }}
              >
                {EXCHANGES.find((e) => e.id === selectedExchange)?.initial}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {EXCHANGES.find((e) => e.id === selectedExchange)?.name} · API credentials
                </div>
                <div className="muted" style={{ fontSize: 11 }}>
                  Read-only is sufficient for sync. Trade scope unlocks one-click execution.
                </div>
              </div>
            </div>

            <div
              style={{
                padding: 18,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 14,
              }}
            >
              <div>
                <label className="muted" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
                  API Key
                </label>
                <input className="se-input mono" placeholder="Paste your API key" />
              </div>
              <div>
                <label className="muted" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
                  API Secret
                </label>
                <input className="se-input mono" type="password" placeholder="••••••••••••" />
              </div>
              <div style={{ gridColumn: "1 / -1", display: "flex", gap: 16 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                  <input type="checkbox" defaultChecked style={{ accentColor: "var(--primary)" }} />
                  Read portfolio
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                  <input type="checkbox" defaultChecked style={{ accentColor: "var(--primary)" }} />
                  Spot trade execution
                </label>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 12,
                    color: "var(--text-faint)",
                  }}
                >
                  <input type="checkbox" disabled />
                  Withdrawals (never enabled)
                </label>
              </div>
            </div>

            <div
              style={{
                padding: "12px 18px",
                background: "rgba(168, 85, 247, 0.04)",
                borderTop: "1px solid var(--border)",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <Icon name="lock" size={14} className="ai-text" />
              <div style={{ fontSize: 11, color: "var(--text-mid)" }}>
                Keys are encrypted at rest with AES-256-GCM. Your secret is never shown again after this step.
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 14,
              gap: 8,
            }}
          >
            <button type="button" className="se-btn">
              Test connection
            </button>
            <button
              type="button"
              className="se-btn se-btn-primary"
              onClick={() => navigate("/", { replace: true })}
            >
              Continue <Icon name="chevronRight" size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
